/**
 * One line on the review screen: counted, on hand, the delta, what it is worth, and
 * any flag it earned.
 *
 * **Every number here is a string the server sent.** Nothing is summed, divided,
 * formatted as currency by arithmetic, or compared against a threshold — the
 * backend did all of that (§ 2.1), and the two swing tests have deliberately
 * different boundary semantics that a client re-implementation would be free to get
 * subtly wrong. The `$` and the `%` are punctuation around a server string, not the
 * result of a calculation.
 *
 * **On-hand appears here and nowhere in entry** (D-1). This is the screen the blind
 * count exists to defer discrepancies to.
 *
 * **The counted quantity is editable here (Kevin's iPad feedback, item 6).** The
 * edit goes through the exact same `PUT …/lines/{item_no}` the entry screen uses —
 * there is no second "review override" field to drift from what actually posts —
 * and the caller re-reads the whole review payload afterwards so every advisory and
 * swing flag is re-evaluated against the new quantity server-side, never guessed at
 * here. Blank still refuses to clear (rule 5): review has no "Clear this count"
 * affordance of its own, so that message points back to the one that does.
 */
import { useEffect, useState } from "react";

import type { ReviewLine } from "../api/types";
import { blurOutcome } from "../entry/entryModel";

const REVIEW_CLEAR_REFUSED_NOTE =
  "Emptying the box does not clear a count. Blank means “not counted,” and this " +
  "screen has no clear action — open this item on the count entry screen and use " +
  "“Clear this count” if you mean to leave its inventory unchanged, or type 0 " +
  "to count it as none.";

/** A dollar figure, or a plain statement that BC had no cost. */
function Money({ value }: { value: string | null }) {
  if (value === null) return <span className="muted">no cost in BC</span>;
  return <>${value}</>;
}

export interface ReviewLineRowProps {
  line: ReviewLine;
  /** Only the responsible manager, and only while staging still accepts changes. */
  editable: boolean;
  /** This line's edit is in flight — disables the field so a second tap cannot race it. */
  saving: boolean;
  /** The server's own refusal for this line's last edit, if there was one. */
  error: string | undefined;
  onEdit: (itemNo: string, value: string) => void;
  /** "Moved in BC after counting: +10 CS" — a warning marker, never a block. */
  movedNote?: string | null;
}

export function ReviewLineRow({ line, editable, saving, error, onEdit, movedNote }: ReviewLineRowProps) {
  const flagged = line.flags.length > 0;
  /**
   * Was this line counted in something other than the item's base unit?
   *
   * Only then does the row spell out units and the base equivalent. On the great
   * majority of lines — every line at St. Pete, and 75 of Ybor Wholesale's 112 —
   * this is false and the row renders exactly as it always did.
   * `counted_base_qty` is null when BC held no conversion factor, in which case
   * there is no equivalent to show and the server already withheld the delta.
   */
  const converted =
    line.counted_base_qty !== null &&
    !!line.base_unit_of_measure &&
    (line.unit_of_measure_code ?? "").toUpperCase() !==
      line.base_unit_of_measure.toUpperCase();
  const [typed, setTyped] = useState<string | undefined>(undefined);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const inputId = `review-qty-${line.item_no}`;

  // The server's value wins once it answers — this is what "re-evaluate against the
  // edited quantity" looks like from one row: there is no client-held quantity that
  // could disagree with what the refetched review payload just said.
  useEffect(() => {
    setTyped(undefined);
  }, [line.counted_qty]);

  function commit() {
    if (typed === undefined) return;
    const outcome = blurOutcome(typed, { counted_qty: line.counted_qty });
    if (outcome.kind === "noop") {
      setTyped(undefined);
      return;
    }
    if (outcome.kind === "clear-refused") {
      setTyped(undefined);
      setMessage(REVIEW_CLEAR_REFUSED_NOTE);
      return;
    }
    if (outcome.kind === "invalid") {
      setMessage(outcome.reason);
      return;
    }
    setMessage(undefined);
    onEdit(line.item_no, outcome.value);
  }

  return (
    <tr
      className={flagged ? "review-row review-row--flagged" : "review-row"}
      data-testid={`review-line-${line.item_no}`}
    >
      <td>
        <div className="review-row__no">{line.item_no}</div>
        {movedNote && (
          <div className="chip chip--warn" data-testid={`review-moved-${line.item_no}`}>
            {movedNote}
          </div>
        )}
        <div className="review-row__desc muted small">{line.description ?? "—"}</div>
        <div className="review-row__marks">
          {line.lot_tracked === true && <span className="chip chip--lot">lot-tracked</span>}
          {line.lot_tracked == null && (
            <span className="chip chip--unknown">lot tracking unknown</span>
          )}
          {line.blocked === true && <span className="chip chip--blocked">blocked in BC</span>}
        </div>
      </td>
      <td className="num review-row__qty">
        {editable ? (
          <>
            <label className="visually-hidden" htmlFor={inputId}>
              Counted quantity for {line.item_no}
            </label>
            <input
              id={inputId}
              className="qty"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              enterKeyHint="done"
              value={typed ?? line.counted_qty}
              disabled={saving}
              onChange={(event) => {
                setTyped(event.target.value);
                setMessage(undefined);
              }}
              onBlur={commit}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  (event.target as HTMLInputElement).blur();
                }
              }}
              data-testid={`review-qty-${line.item_no}`}
            />
            {saving && (
              <span className="sync sync--pending" data-testid={`review-saving-${line.item_no}`}>
                saving…
              </span>
            )}
            <span className="muted small"> {line.unit_of_measure_code ?? ""}</span>
            {converted && (
              <span className="muted small" data-testid={`review-base-${line.item_no}`}>
                {" "}
                ({line.counted_base_qty} {line.base_unit_of_measure})
              </span>
            )}
            {(message ?? error) && (
              <p
                className={error ? "error-text small" : "muted small"}
                data-testid={`review-qty-msg-${line.item_no}`}
              >
                {message ?? error}
              </p>
            )}
          </>
        ) : (
          <>
            {line.counted_qty}
            <span className="muted small"> {line.unit_of_measure_code ?? ""}</span>
            {converted && (
              <span className="muted small" data-testid={`review-base-${line.item_no}`}>
                {" "}
                ({line.counted_base_qty} {line.base_unit_of_measure})
              </span>
            )}
          </>
        )}
      </td>
      {/*
        On-hand and delta are BASE quantities — that is what Business Central
        holds and what it will move. On a line counted in another unit they are
        labelled, because "4" beside "150" with no units is how a manager
        mis-reads a variance (item 3).
      */}
      <td className="num muted" data-testid={`on-hand-${line.item_no}`}>
        {line.on_hand_qty}
        {converted && <span className="muted small"> {line.base_unit_of_measure}</span>}
      </td>
      <td className="num" data-testid={`delta-${line.item_no}`}>
        <strong>{line.delta_qty}</strong>
        {converted && <span className="muted small"> {line.base_unit_of_measure}</span>}
      </td>
      <td className="num">
        <Money value={line.counted_value} />
      </td>
      <td className="num">
        <Money value={line.delta_value} />
      </td>
      <td>
        {flagged ? (
          <ul className="flag-list" data-testid={`flags-${line.item_no}`}>
            {line.flags.map((flag) => (
              <li key={flag.test}>
                <span className={`chip chip--flag chip--${flag.test.replace("_", "-")}`}>
                  {flag.test === "quantity_variance" ? "quantity" : "cost"}
                </span>{" "}
                {/* The server's own sentence, verbatim: it names the observation and
                    the bar it was measured against, which is what a manager is being
                    asked to look at twice. */}
                <span className="small">{flag.summary}</span>
              </li>
            ))}
          </ul>
        ) : (
          <span className="muted small">—</span>
        )}
      </td>
    </tr>
  );
}
